import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { ConfirmarDialog, FP, PageHeader, fpInputSx } from 'components/financepro';
import { formatearFecha, cardShellSx, btnPrimarySx } from './entradaDiarioShared';
import {
  ESTADO_DIA,
  PROGRAMA_CONTABILIZACION_DIARIA,
  SEMAFORO_PROPUESTA,
  agruparPorTipo,
  fmtMoney,
  resumenPrevista,
} from './contabilizacionDiariaShared';
import SoloNoFiscal from './SoloNoFiscal';

const filaEncabezadoSx = {
  '& td': {
    border: 'none',
    borderBottom: `2px solid ${FP.color.border}`,
    bgcolor: FP.color.surfaceAlt,
    fontWeight: 700,
    fontSize: '0.72rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
};

const COLOR = {
  ok: { bg: FP.color.successSoft, fg: FP.color.successDark },
  aviso: { bg: '#FFF4E0', fg: '#9A6200' },
  error: { bg: FP.color.dangerSoft, fg: FP.color.danger },
  neutro: { bg: FP.color.surfaceAlt, fg: FP.color.textMuted },
};

function Etiqueta({ tono, children }) {
  const c = COLOR[tono] || COLOR.neutro;
  return <Chip size="small" label={children} sx={{ fontWeight: 700, bgcolor: c.bg, color: c.fg }} />;
}

function Propuesta({ p }) {
  const [abierta, setAbierta] = useState(p.semaforo !== 'listo');
  const s = SEMAFORO_PROPUESTA[p.semaforo] || SEMAFORO_PROPUESTA.listo;
  return (
    <Box sx={{ borderTop: `1px solid ${FP.color.border}` }}>
      <Box
        onClick={() => setAbierta((v) => !v)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, cursor: 'pointer' }}
      >
        <IconButton size="small">{abierta ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }} noWrap title={p.concepto}>
            {p.concepto}
          </Typography>
          {p.motivo && (
            <Typography sx={{ fontSize: '0.78rem', color: COLOR[s.tono].fg }}>{p.motivo}</Typography>
          )}
          {p.fecha_ajustada && (
            <Typography sx={{ fontSize: '0.78rem', color: COLOR.aviso.fg }}>
              Mes contable cerrado: entra con fecha {formatearFecha(p.fecha)}
            </Typography>
          )}
        </Box>
        {p.reverso && <Etiqueta tono="aviso">Reverso</Etiqueta>}
        <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>${fmtMoney(p.debito)}</Typography>
        <Etiqueta tono={s.tono}>{s.etiqueta}</Etiqueta>
      </Box>
      <Collapse in={abierta} unmountOnExit>
        <Box sx={{ px: 2, pb: 1.5, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableBody>
              <TableRow sx={filaEncabezadoSx}>
                <TableCell>Cuenta</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Concepto</TableCell>
                <TableCell align="right">Débito</TableCell>
                <TableCell align="right">Crédito</TableCell>
              </TableRow>
              {p.lineas.map((ln) => (
                <TableRow key={`${ln.regla}-${ln.cuenta}`}>
                  <TableCell sx={{ fontWeight: 700, color: ln.cuenta ? undefined : FP.color.danger }}>
                    {ln.cuenta || 'Sin cuenta'}
                  </TableCell>
                  <TableCell>{ln.nombre || '—'}</TableCell>
                  <TableCell sx={{ color: FP.color.textMuted }}>{ln.etiqueta}</TableCell>
                  <TableCell align="right">{ln.debito ? `$${fmtMoney(ln.debito)}` : ''}</TableCell>
                  <TableCell align="right">{ln.credito ? `$${fmtMoney(ln.credito)}` : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
}

function ContabilizacionDiariaContenido() {
  const { controller, showError, showSuccess } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer =
    tienePermisosModulo(roleDetalles, PROGRAMA_CONTABILIZACION_DIARIA) ||
    puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_CONTABILIZACION_DIARIA);
  const puedeModificar = puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_CONTABILIZACION_DIARIA);

  const [estado, setEstado] = useState(null); // { activa, fechaarranque }
  const [arranque, setArranque] = useState('');
  const [dias, setDias] = useState([]);
  const [dia, setDia] = useState('');
  const [prevista, setPrevista] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [cargandoDias, setCargandoDias] = useState(true);
  // El aviso flotante desaparece solo: un fallo al cargar se queda en pantalla.
  const [error, setError] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [envioADeshacer, setEnvioADeshacer] = useState(null);

  const llamar = useCallback(
    async (ruta, cuerpo = {}) => {
      const response = await fetch(`${servidor}/contabilidadbackend_contdiaria_${ruta}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuario: usuarioLog || '', password: claveLog || '', ...cuerpo }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.Error || 'Error en la contabilización diaria');
      return data;
    },
    [servidor, usuarioLog, claveLog]
  );

  const cargarDias = useCallback(async () => {
    setCargandoDias(true);
    try {
      const data = await llamar('dias');
      setDias(data.dias || []);
      return data.dias || [];
    } finally {
      setCargandoDias(false);
    }
  }, [llamar]);

  const cargarPrevista = useCallback(
    async (d) => {
      if (!d) return;
      setCargando(true);
      try {
        setPrevista(await llamar('prevista', { dia: d }));
        setError('');
      } catch (e) {
        showError(e.message);
        setError(e.message);
        setPrevista(null);
      } finally {
        setCargando(false);
      }
    },
    [llamar, showError]
  );

  useEffect(() => {
    if (!puedeLeer || !usuarioLog) return;
    (async () => {
      try {
        const e = await llamar('estado');
        setEstado(e);
        setArranque(e.fechaarranque || '');
        if (e.activa && e.fechaarranque) {
          const lista = await cargarDias();
          const primero = lista.find((x) => x.estado === 'pendiente' || x.estado === 'novedades') || lista[0];
          if (primero) setDia(primero.dia);
        }
      } catch (err) {
        showError(err.message);
        setError(err.message);
        setCargandoDias(false);
      }
    })();
  }, [puedeLeer, usuarioLog]);

  useEffect(() => {
    cargarPrevista(dia);
  }, [dia]);

  const resumen = useMemo(() => resumenPrevista(prevista?.propuestas || []), [prevista]);
  const grupos = useMemo(() => agruparPorTipo(prevista?.propuestas || []), [prevista]);

  const tras = async (mensaje) => {
    showSuccess(mensaje);
    await Promise.all([cargarDias(), cargarPrevista(dia)]);
  };

  const guardarArranque = async () => {
    setProcesando(true);
    try {
      const data = await llamar('guardararranque', { fecha: arranque });
      setEstado((e) => ({ ...e, fechaarranque: data.fechaarranque }));
      const lista = await cargarDias();
      if (lista[0]) setDia(lista[0].dia);
      showSuccess('Fecha de arranque guardada');
    } catch (e) {
      showError(e.message);
    } finally {
      setProcesando(false);
    }
  };

  const enviar = async () => {
    setProcesando(true);
    try {
      const data = await llamar('enviar', { dia });
      setConfirmarEnvio(false);
      await tras(`Envío ${data.envio}: ${data.asientos} asiento(s) en la contabilidad no fiscal`);
    } catch (e) {
      showError(e.message);
    } finally {
      setProcesando(false);
    }
  };

  const deshacer = async () => {
    setProcesando(true);
    try {
      await llamar('deshacer', { envio: envioADeshacer.envio });
      setEnvioADeshacer(null);
      await tras('Envío deshecho: sus asientos vuelven a la pre-vista');
    } catch (e) {
      showError(e.message);
    } finally {
      setProcesando(false);
    }
  };

  const aviso = (texto, severidad = 'info') => (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader title="Contabilización diaria" subtitle={nombreNegocio || ''} />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert severity={severidad}>{texto}</Alert>
      </Box>
    </Box>
  );

  if (!puedeLeer) return aviso('No tiene permisos para esta pantalla.');
  if (estado && !estado.activa) {
    return aviso('Este negocio no usa la contabilización diaria. Se activa en Configuración → Parámetros.');
  }

  if (estado && !estado.fechaarranque) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Contabilización diaria" subtitle={nombreNegocio || ''} />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Card elevation={0} sx={{ ...cardShellSx, p: 2.5, maxWidth: 640 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Fecha de arranque</Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem', mb: 2 }}>
              Primer día cuyas operaciones se contabilizan desde aquí. Lo anterior se ignora. Una vez
              enviado el primer día ya no se puede cambiar.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="date"
                size="small"
                label="Desde"
                value={arranque}
                onChange={(e) => setArranque(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ ...fpInputSx, width: 200 }}
              />
              <Button
                variant="contained"
                disabled={!puedeModificar || procesando || !arranque}
                onClick={guardarArranque}
                sx={btnPrimarySx}
              >
                Guardar
              </Button>
            </Box>
          </Card>
        </Box>
      </Box>
    );
  }

  const enviosActivos = (prevista?.envios || []).filter((e) => e.estado === 'A');

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Contabilización diaria"
        subtitle={[nombreNegocio, dia ? `Pre-vista del ${formatearFecha(dia)}` : ''].filter(Boolean).join(' · ')}
        cta={
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!puedeModificar || procesando || resumen.listos === 0}
            onClick={() => setConfirmarEnvio(true)}
            sx={{ ...btnPrimarySx, height: 42 }}
          >
            Enviar a contabilidad ({resumen.listos})
          </Button>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Card elevation={0} sx={{ ...cardShellSx, maxHeight: { md: '78vh' }, overflowY: 'auto' }}>
              {cargandoDias && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
              {!cargandoDias && dias.length === 0 && (
                <Typography sx={{ p: 2, color: FP.color.textMuted, fontSize: '0.85rem' }}>
                  No hay días para mostrar.
                </Typography>
              )}
              <List dense disablePadding>
                {dias.map((d) => {
                  const e = ESTADO_DIA[d.estado] || ESTADO_DIA.sinmovimientos;
                  return (
                    <ListItemButton
                      key={d.dia}
                      selected={d.dia === dia}
                      onClick={() => setDia(d.dia)}
                      sx={{ justifyContent: 'space-between', borderBottom: `1px solid ${FP.color.border}` }}
                    >
                      <Typography sx={{ fontWeight: d.dia === dia ? 700 : 500, fontSize: '0.88rem' }}>
                        {formatearFecha(d.dia)}
                      </Typography>
                      <Etiqueta tono={e.tono}>
                        {d.pendientes > 0 ? `${e.etiqueta} (${d.pendientes})` : e.etiqueta}
                      </Etiqueta>
                    </ListItemButton>
                  );
                })}
              </List>
            </Card>
          </Grid>

          <Grid item xs={12} md={9}>
            {cargando && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!cargando && prevista && (
              <>
                {resumen.total === 0 && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    No hay nada pendiente de contabilizar en este día.
                  </Alert>
                )}
                {resumen.conProblema > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {resumen.conProblema} asiento(s) no se pueden enviar todavía. Corrija la configuración
                    (cuenta de la categoría en Gastos → Categorías, cuentas de las reglas en Config. Documento
                    Contable, cuenta de la forma de pago) y vuelva a abrir el día. Los demás se pueden enviar.
                  </Alert>
                )}
                {prevista.cuadre && resumen.total > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Efecto en caja (efectivo) de esta pre-vista: ${fmtMoney(prevista.caja_efectivo_prevista)} ·
                    Según las operaciones del día: cobrado en efectivo ${fmtMoney(prevista.cuadre.cobrado_efectivo)},
                    gastos ${fmtMoney(prevista.cuadre.gastos)}.
                  </Alert>
                )}

                {grupos.map((g) => (
                  <Card key={g.tipo} elevation={0} sx={{ ...cardShellSx, mb: 2 }}>
                    <Box sx={{ px: 2, py: 1.4, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontWeight: 800 }}>
                        {g.tipo} · {g.propuestas.length} asiento(s)
                      </Typography>
                      <Typography sx={{ fontWeight: 700 }}>${fmtMoney(g.total)}</Typography>
                    </Box>
                    {g.propuestas.map((p) => (
                      <Propuesta key={p.clave} p={p} />
                    ))}
                  </Card>
                ))}

                {(prevista.envios || []).length > 0 && (
                  <Card elevation={0} sx={{ ...cardShellSx, p: 2 }}>
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>Envíos de este día</Typography>
                    {prevista.envios.map((e) => (
                      <Box
                        key={e.envio}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.6, flexWrap: 'wrap' }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>Envío {e.envio}</Typography>
                        <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem', flex: 1 }}>
                          {e.fecha} · {e.usuario} · {e.asientos} asiento(s)
                        </Typography>
                        <Etiqueta tono={e.estado === 'A' ? 'ok' : e.estado === 'R' ? 'neutro' : 'aviso'}>
                          {e.estado === 'A' ? 'Aplicado' : e.estado === 'R' ? 'Deshecho' : 'Incompleto'}
                        </Etiqueta>
                        {e.estado === 'A' && (
                          <Tooltip title="Saca sus asientos del diario (si ninguno pasó a fiscal ni fue conciliado)">
                            <span>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<UndoIcon />}
                                disabled={!puedeModificar || procesando}
                                onClick={() => setEnvioADeshacer(e)}
                                sx={{ textTransform: 'none' }}
                              >
                                Deshacer
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                    {enviosActivos.length > 0 && resumen.total > 0 && (
                      <Typography sx={{ color: FP.color.textMuted, fontSize: '0.8rem', mt: 1 }}>
                        Lo que aparece arriba son novedades posteriores al envío: documentos registrados tarde
                        o anulados (reversos).
                      </Typography>
                    )}
                  </Card>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Box>

      <ConfirmarDialog
        abierto={confirmarEnvio}
        titulo={`Enviar el ${formatearFecha(dia)} a contabilidad`}
        textoConfirmar={`Enviar ${resumen.listos} asiento(s)`}
        cargando={procesando}
        onCerrar={() => setConfirmarEnvio(false)}
        onConfirmar={enviar}
      >
        <Typography sx={{ mb: 1 }}>
          Entran a la versión no fiscal por ${fmtMoney(resumen.montoListos)}.
        </Typography>
        {resumen.conProblema > 0 && (
          <Alert severity="warning">
            {resumen.conProblema} asiento(s) con problemas se quedan pendientes en la pre-vista.
          </Alert>
        )}
      </ConfirmarDialog>

      <ConfirmarDialog
        abierto={Boolean(envioADeshacer)}
        titulo={`Deshacer el envío ${envioADeshacer?.envio || ''}`}
        mensaje="Sus asientos salen del diario y los documentos vuelven a la pre-vista de este día."
        textoConfirmar="Deshacer"
        destructivo
        cargando={procesando}
        onCerrar={() => setEnvioADeshacer(null)}
        onConfirmar={deshacer}
      />
    </Box>
  );
}

/** Se opera desde la versión no fiscal, que es adonde entran los asientos. */
export default function ContabilizacionDiaria() {
  return (
    <SoloNoFiscal titulo="Contabilización diaria">
      <ContabilizacionDiariaContenido />
    </SoloNoFiscal>
  );
}
